import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import LocalPrivacySiteModal from "../../LocalPrivacySiteModal/LocalPrivacySiteModal";
import Default from "./ManagePrivacyContent/Default";
import CountrySpecific from "./ManagePrivacyContent/CountrySpecific";
import "./ManagePrivacy.scss";
import { UtilsUser } from "../../../../../utils/UtilsUser";
import { useMyDataHooks } from "../../../../../hooks/mydata-hooks";
import { MyDataState } from "../../../../../store/reducers/mydata-reducer";
import { UserState } from "../../../../../store/reducers/user-reducer";
import { useIntersectionObserver } from "../../../../../hooks/intersection-hooks";
import { useHub } from "../../../../../hooks/hub-hooks";
import FocusTrapping from "../../../../../components/__hoc/FocusTrapping";
import { LayoutState } from "../../../../../store/reducers/layout-reducer";

const ManagePrivacy: React.FC = () => {
  const { history, historyReqBody } = useSelector<
    ApplicationState,
    MyDataState
  >((state) => state.myData);
  const { languageTrans } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { userData } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const [isCountryListModalOpen, setIsCountryListModalOpen] =
    useState<boolean>(false);
  const { loadMyDataHistory } = useMyDataHooks();
  const { hasLocalSite } = useHub();
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, {
    rootMargin: "0px",
    threshold: 0.8,
  });

  const removeSnapScroll = () => {
    if (!visible) {
      return;
    }
    const el = document.getElementById("main-body");
    el?.classList.remove("snap-scroll");
  };

  const addSnapScroll = () => {
    const el = document.getElementById("main-body");
    if (el?.scrollTop === 0) {
      el.classList.add("snap-scroll");
    }
  };

  const renderContent = () => {
    // Scenario 1
    // if (!hasSelectedCountryCookie) {
    //   return <Default setModalOpen={setIsCountryListModalOpen} />;
    // }

    // Scenario 2
    if (!hasLocalSite) {
      return (
        <CountrySpecific
          setModalOpen={setIsCountryListModalOpen}
          hasLocalSite={hasLocalSite}
        />
      );
    }

    // Scenario 3
    if (
      (UtilsUser.isUserLoggedIn() && history.request_count === 0) ||
      !UtilsUser.isUserLoggedIn()
    ) {
      return <Default setModalOpen={setIsCountryListModalOpen} />;
    } else {
      return (
        <CountrySpecific
          setModalOpen={setIsCountryListModalOpen}
          hasLocalSite={hasLocalSite}
        />
      );
    }
  };

  useEffect(() => {
    if (
      UtilsUser.isUserLoggedIn() &&
      userData.loginId &&
      languageTrans !== ""
    ) {
      loadMyDataHistory(historyReqBody);
    }
  }, [userData.loginId, languageTrans]);

  useEffect(() => {
    removeSnapScroll();
  }, [visible]);

  useEffect(() => {
    const removeTabindexOnSliderFrames = () => {
      // Get all container with slider-frames class
      const frames = ref.current?.querySelectorAll(".slider-frame");
      // Set tabindex to -1 for each frame element in the container
      if (frames) {
        frames.forEach((frame) => {
          frame.setAttribute("tabIndex", "-1");
        });
      }
    };

    // Remove tabindex on slider frames when component mounts and when there are changes in the DOM tree of the slider frames container.
    removeTabindexOnSliderFrames();
    // Create a mutation observer to observe changes in the DOM tree of the slider frames container. When changes occur, call the removeTabindexOnSliderFrames
    const observer = new MutationObserver(() => {
      removeTabindexOnSliderFrames();
    });

    // Start observing the slider frames container for changes. The observer will be disconnected when the component unmounts.
    if (ref.current) {
      observer.observe(ref.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["tabIndex"],
      });
    }

    document
      .getElementById("main-body")
      ?.addEventListener("scroll", addSnapScroll);

    return () => {
      document
        .getElementById("main-body")
        ?.removeEventListener("scroll", addSnapScroll);

      // Disconnect the observer
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <div
        className="manage-privacy"
        id="manage-privacy"
        ref={ref}
        data-testid="manage-privacy"
      >
        {renderContent()}
      </div>
      <FocusTrapping
        isActive={isCountryListModalOpen}
        onHandleClose={() => setIsCountryListModalOpen(false)}
        modalId="local-privacy-modal"
      >
        <LocalPrivacySiteModal
          show={isCountryListModalOpen}
          onClickClose={() => setIsCountryListModalOpen(false)}
        />
      </FocusTrapping>
    </>
  );
};

export default ManagePrivacy;
